import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useScan } from '../../contexts/ScanContext';
import { useNavigate } from 'react-router-dom';

const RepositoryDetail = () => {
  const { owner, repo } = useParams(); // Get owner/repo from URL
  const {
    selectedRepo,
    repoScans,
    repoStats,
    loadingRepoDetails,
    error,
    startRepoScan,
    analyzing,
    scanProgress,
    scanLogs,
    scanStage,
    selectRepository, // Get selectRepository from context
  } = useScan();

  const navigate = useNavigate();

  useEffect(() => {
    const repoUrl = `https://github.com/${owner}/${repo}`;
    // If there's no selected repo or it's the wrong one, select it
    if (!selectedRepo || selectedRepo.repo_url !== repoUrl) {
      selectRepository(repoUrl);
    }
  }, [owner, repo, selectedRepo, selectRepository]);


  if (!selectedRepo || loadingRepoDetails) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-center p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Repository Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
              {selectedRepo.repo_url.split('/').pop()}
            </h2>
            <p className="text-gray-600 break-all">{selectedRepo.repo_url}</p>
          </div>
          <button
            onClick={() => navigate('/repositories')}
            className="w-full sm:w-auto flex-shrink-0 px-4 py-2 min-h-[44px] rounded-md text-white font-medium bg-teal-600 hover:bg-teal-700"
          >
            Back to Repositories
          </button>
        </div>

        {/* Stats Overview */}
        {repoStats && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600">Total Scans</div>
              <div className="text-2xl font-bold text-gray-900">{repoStats.total_scans}</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600">Average Score</div>
              <div className="text-2xl font-bold text-gray-900">
                {repoStats.average_security_score.toFixed(1)}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600">Vulnerabilities</div>
              <div className="text-2xl font-bold text-gray-900">
                {Object.values(repoStats.vulnerability_trend).reduce((a, b) => a + b, 0)}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600">Latest Fix</div>
              <div className="text-sm font-medium text-gray-900">
                {repoStats.latest_fixes[0]?.fixed_at
                  ? new Date(repoStats.latest_fixes[0].fixed_at).toLocaleDateString()
                  : 'None'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Current Scan Progress */}
      {analyzing && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Current Scan Progress</h3>
          <div className="space-y-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-teal-500 h-2.5 rounded-full"
                style={{ width: `${scanProgress}%` }}
              ></div>
            </div>
            <div className="text-sm text-gray-600">{scanStage}</div>
            <div className="space-y-2">
              {scanLogs.map((log, index) => (
                <div key={index} className="text-sm text-gray-600">
                  {log.icon} {log.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Scan History */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Scan History</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {repoScans.map((scan) => (
            <div key={scan.scan_id} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Scan {scan.scan_id.slice(0, 8)}...
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(scan.start_time).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-xs text-gray-600">
                      {scan.vulnerability_count} vulnerabilities
                    </div>
                  </div>
                  <button
                    className="ml-4 px-3 py-1 bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors text-xs font-semibold"
                    onClick={() => navigate(`/results/${scan.scan_id}`)}
                  >
                    View Results
                  </button>
                </div>
              </div>
              {scan.security_score !== null && (
                <div className="mt-2 text-sm text-gray-600">
                  Security Score: {scan.security_score}
                </div>
              )}
            </div>
          ))}
          {repoScans.length === 0 && (
            <div className="p-4 text-center text-gray-500">
              No scan history available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RepositoryDetail;
